import { useEffect, useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import Cookies from "js-cookie"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { env } from "@/env"
import { sendMfaOtp, verifyOtp } from "@/lib/api/auth"
import { getUserEmail } from "@/lib/auth"
import { toast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp"
import { Icons } from "@/components/icons"

export const Route = createFileRoute("/_auth/mfa-auth")({
  component: Page,
})

const FormSchema = z.object({
  pin: z.string().min(6, {
    message: "Your one-time password must be 6 characters.",
  }),
})

export function Page() {
  const navigate = useNavigate()
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)
  const [canResend, setCanResend] = useState(false)
  const email = getUserEmail()
  const mfaAuthCookie = `mfa-auth-${email}`

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      pin: "",
    },
  })

  useEffect(() => {
    const interval = setInterval(() => {
      const otpSetTime = Cookies.get("otp-set-time")

      if (otpSetTime) {
        const otpSetTimestamp = Number(otpSetTime)
        const currentTime = new Date().getTime()
        const expirationTime = 5 * 60 * 1000
        const remaining =
          (otpSetTimestamp + expirationTime - currentTime) / 1000

        setTimeRemaining(remaining > 0 ? remaining : null)
        setCanResend(remaining <= 0)
      } else {
        setTimeRemaining(null)
        setCanResend(true)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const mutation = useMutation({
    mutationFn: async (otp: string) => {
      await verifyOtp(otp)
    },
    onSuccess: async () => {
      Cookies.set(mfaAuthCookie, "true", {
        expires: (1 / 1440) * env.SESSION_DURATION,
      })
      navigate({ to: "/" })
    },
    onError: (error) => {
      toast({
        title: "Incorrect OTP",
        description: `${error}`,
        variant: "destructive",
      })
    },
  })

  function onSubmit(data: z.infer<typeof FormSchema>) {
    mutation.mutate(data.pin)
  }

  async function resendCode() {
    if (canResend) {
      try {
        await sendMfaOtp()

        toast({
          title: "OTP Resent",
          description: "A new code has been sent to your email.",
        })

        setCanResend(false)
      } catch (error) {
        toast({
          title: "Error",
          description:
            "There was an error resending the OTP. Please try again.",
          variant: "destructive",
        })
      }
    }
  }

  return (
    <Form {...form}>
      <div className="flex flex-col items-center">
        <Icons.himo className="mt-19" height="120px" width="120px" />
      </div>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="z-20 w-full space-y-6"
      >
        <FormField
          control={form.control}
          name="pin"
          render={({ field }) => (
            <FormItem className="flex flex-col items-center text-center">
              <FormLabel className="text-lg font-semibold text-primary">
                MFA Authentication
              </FormLabel>
              <FormControl>
                <InputOTP maxLength={6} {...field}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              <FormDescription className="font-gilroy-thin w-52 text-[12px]">
                Please enter the one-time password sent to your email.
              </FormDescription>
              <FormMessage />
              {timeRemaining !== null && (
                <p className="py-2 text-sm text-muted-foreground">
                  Expires in: {Math.ceil(timeRemaining)} seconds
                </p>
              )}
            </FormItem>
          )}
        />

        <div className="flex justify-center">
          <Button type="submit" className="w-60 rounded-full">
            Submit
          </Button>
        </div>

        <div className="flex items-center justify-center text-[12px] text-muted-foreground">
          Didn&apos;t receive a code?{" "}
          <Button
            type="button"
            onClick={resendCode}
            disabled={!canResend}
            variant="link"
            className="text-primary"
          >
            Resend
          </Button>
        </div>
      </form>
      <div className="mt-10 flex items-center justify-center space-x-2 md:justify-end">
        <p className="font-gilroy-thin text-[16px]">By</p>
        <Icons.hirayaWText width="90" />
      </div>
    </Form>
  )
}
